<template>
    <v-slide-x-transition mode="out-in">
        <v-container v-if="showComponent" style="max-width: 1200px">
            <CardTitle
                :title="
                    !prefill
                        ? $t('register.title')
                        : $t('register.companyUpdate')
                "
                :link="{
                    href: '/login',
                    text: !prefill ? $t('register.alreadyAccount') : '',
                }"
            />
            <v-form ref="form">
                <v-card class="pa-5 mt-2">
                    <v-card-title>
                        {{ $t("register.companyInformation") }}
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="companyInformation.name"
                                    data-cy="register-company-name"
                                    v-auto-animate
                                    :label="$t('register.companyName')"
                                    :rules="companyNameRules"
                                    hide-details="auto"
                                    variant="outlined"
                                    validate-on="lazy"
                                    density="compact"
                                    :error-messages="
                                        errors.companyInformation?.name
                                            ? errors.companyInformation.name[0]
                                            : ''
                                    "
                                ></v-text-field>
                            </v-col>
                            <v-col>
                                <v-select
                                    v-model="companyInformation.type"
                                    data-cy="register-company-type"
                                    :label="$t('register.companyType.title')"
                                    :items="companyTypes"
                                    item-title="text"
                                    item-value="value"
                                    hide-details="auto"
                                    density="compact"
                                ></v-select>
                            </v-col>
                            <v-col>
                                <v-select
                                    v-model="
                                        companyInformation.subscriptionMonths
                                    "
                                    :label="$t('register.subscriptionPeriod')"
                                    :items="subscriptionPeriods"
                                    :suffix="$t('register.subscriptionMonths')"
                                    item-title="text"
                                    item-value="value"
                                    hide-details="auto"
                                    density="compact"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-auto-animate>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="companyInformation.VATNumber"
                                    data-cy="register-company-vat"
                                    :rules="vatRules"
                                    :label="$t('register.VATNumber')"
                                    hide-details="auto"
                                    maxlength="14"
                                    validate-on="blur"
                                    density="compact"
                                    :error-messages="
                                        errors.companyInformation?.VATNumber
                                            ? errors.companyInformation
                                                  .VATNumber[0]
                                            : ''
                                    "
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                md="3"
                                v-if="
                                    companyInformation.VATNumber.startsWith(
                                        'RO'
                                    )
                                "
                                class="ma-0"
                            >
                                <!-- Group: Registry order number, unique id, year -->
                                <v-row class="align-center">
                                    <v-col
                                        cols="12"
                                        md="4"
                                        sm="4"
                                        class="mr-0 pr-0"
                                    >
                                        <v-text-field
                                            ref="registryOrderNumber"
                                            class="order-number"
                                            v-model="
                                                companyInformation.registry
                                                    .orderNumber
                                            "
                                            @blur="validateRegistryNumber"
                                            placeholder="Jxx"
                                            hide-details="auto"
                                            maxlength="3"
                                            density="compact"
                                            :rules="[]"
                                            :error="
                                                !!registryGroupErrors.orderNumber
                                            "
                                            :error-messages="[]"
                                        >
                                            <template v-slot:append-inner>
                                                /
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col
                                        cols="12"
                                        md="4"
                                        sm="4"
                                        class="ml-0 pl-0 mr-0 pr-0"
                                    >
                                        <v-text-field
                                            ref="registryUniqueId"
                                            tile
                                            class="unique-id"
                                            v-model="
                                                companyInformation.registry
                                                    .uniqueId
                                            "
                                            placeholder="xxxxx"
                                            @blur="validateRegistryNumber"
                                            maxlength="5"
                                            hide-details="auto"
                                            density="compact"
                                            :rules="[]"
                                            :error="
                                                !!registryGroupErrors.uniqueId
                                            "
                                            :error-messages="[]"
                                        >
                                            <template v-slot:append-inner>
                                                /
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col
                                        cols="12"
                                        md="4"
                                        sm="4"
                                        class="ml-0 pl-0"
                                    >
                                        <v-text-field
                                            ref="registryYear"
                                            class="year"
                                            v-model="
                                                companyInformation.registry.year
                                            "
                                            placeholder="2xxx"
                                            @blur="validateRegistryNumber"
                                            hide-details="auto"
                                            density="compact"
                                            maxlength="4"
                                            :rules="[]"
                                            :error="!!registryGroupErrors.year"
                                            :error-messages="[]"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <div
                                    v-if="registryGroupErrorList.length"
                                    class="registry-errors mt-1"
                                >
                                    <div
                                        v-for="(
                                            msg, idx
                                        ) in registryGroupErrorList"
                                        :key="idx"
                                        class="v-messages__message text-error text-caption"
                                    >
                                        {{ $t(msg) }}
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="12" md="1">
                                <DownloadFileButton
                                    :tooltip-text="
                                        $t('register.downloadContract')
                                    "
                                    document="initialContract"
                                    :multi-lang="true"
                                />
                            </v-col>
                            <v-col cols="12" md="5">
                                <!-- add a fileupload for legal documents -->
                                <v-chip
                                    v-for="f in companyInformation.files"
                                    :key="f.name"
                                    class="me-2 mb-1"
                                    color="primary"
                                    size="small"
                                    label
                                    closable
                                    @click:close.stop="removeFile(f.name)"
                                >
                                    {{ f.name }}
                                </v-chip>
                                <!-- Error when user tries to submit too few docs -->
                                <p
                                    v-if="fileErrors"
                                    class="text-error text-body-2 mt-1"
                                >
                                    {{ fileErrors }}
                                </p>
                                <!-- Picker is rendered only while we still need files -->
                                <v-file-input
                                    v-if="showFileInput"
                                    :key="fileInputKey"
                                    data-cy="register-company-documents"
                                    accept=".pdf"
                                    multiple
                                    hide-details="auto"
                                    density="compact"
                                    prepend-icon=""
                                    append-inner-icon="mdi-file-upload"
                                    :label="$t('register.legalDocuments')"
                                    :hint="displayNecessaryDocumentsHint"
                                    persistent-hint
                                    @update:modelValue="handleFileSelect"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="companyInformation.address.country"
                                    data-cy="register-company-country"
                                    :label="$t('register.country')"
                                    hide-details="auto"
                                    density="compact"
                                    :rules="addressRules"
                                ></v-text-field>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    v-model="companyInformation.address.city"
                                    data-cy="register-company-city"
                                    :label="$t('register.city')"
                                    hide-details="auto"
                                    density="compact"
                                    :rules="addressRules"
                                ></v-text-field>
                            </v-col>

                            <v-col>
                                <v-text-field
                                    v-model="
                                        companyInformation.address.postalCode
                                    "
                                    data-cy="register-company-postal-code"
                                    :label="$t('register.postalCode')"
                                    hide-details="auto"
                                    density="compact"
                                    :rules="addressRules"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="companyInformation.address.street"
                                    data-cy="register-company-street"
                                    :label="$t('register.street')"
                                    hide-details="auto"
                                    density="compact"
                                ></v-text-field>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    v-model="companyInformation.address.number"
                                    data-cy="register-company-number"
                                    :label="$t('register.number')"
                                    hide-details="auto"
                                    density="compact"
                                ></v-text-field>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    v-model="companyInformation.address.fax"
                                    :label="$t('register.fax')"
                                    hide-details="auto"
                                    density="compact"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="companyInformation.website"
                                    :label="$t('register.website')"
                                    hide-details="auto"
                                    :rules="websiteRules"
                                    validate-on="blur"
                                    density="compact"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
                <v-card class="pa-5 mt-5">
                    <v-card-title>
                        {{ $t("register.administratorInformation") }}
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="contact.firstName"
                                    data-cy="register-contact-first-name"
                                    v-auto-animate
                                    :rules="nameRules"
                                    :label="$t('register.firstName')"
                                    hide-details="auto"
                                    density="compact"
                                ></v-text-field>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    v-model="contact.lastName"
                                    data-cy="register-contact-last-name"
                                    v-auto-animate
                                    :rules="nameRules"
                                    :label="$t('register.lastName')"
                                    hide-details="auto"
                                    density="compact"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="contact.position"
                                    data-cy="register-contact-position"
                                    :rules="positionRules"
                                    :label="$t('register.position')"
                                    hide-details="auto"
                                    density="compact"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex align-center">
                                <v-select
                                    :label="$t('register.prefix')"
                                    style="max-width: 100px; margin-right: 10px"
                                    :items="europeanCountryCodes"
                                    item-title="code"
                                    item-value="dial_code"
                                    density="compact"
                                    v-model="contact.phone_prefix"
                                    hide-details="auto"
                                >
                                    <template #item="{ props, item }">
                                        <v-list-item v-bind="props">
                                            <v-list-item-title>
                                            </v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </v-select>
                                <v-text-field
                                    v-model="contact.phone_number"
                                    data-cy="register-contact-phone"
                                    :label="$t('register.phoneNumber')"
                                    :rules="phoneNumberRules"
                                    validate-on="input"
                                    density="compact"
                                    hide-details="auto"
                                >
                                    <template v-slot:prepend-inner>
                                        {{ getCountryDialCode }}
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <SelectSpokenLanguages
                                    v-model="contact.spoken_languages"
                                    density="compact"
                                />
                            </v-col>
                        </v-row>
                        <v-divider class="my-4" />
                        <v-row>
                            <v-col v-if="!firebaseUid" cols="12" md="6">
                                <v-row>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="contact.email"
                                            data-cy="register-contact-email"
                                            :rules="emailRules"
                                            :label="$t('register.email')"
                                            autocomplete="email"
                                            hide-details="auto"
                                            density="compact"
                                            :error-messages="
                                                errors.contact?.email
                                                    ? errors.contact.email[0]
                                                    : ''
                                            "
                                            :error="!!errors.contact?.email"
                                            validate-on="blur"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="password"
                                            data-cy="register-password"
                                            :rules="passwordRules"
                                            :label="$t('register.password')"
                                            type="password"
                                            autocomplete="new-password"
                                            hide-details="auto"
                                            density="compact"
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="confirmPassword"
                                            data-cy="register-confirm-password"
                                            :rules="confirmPasswordRules"
                                            :label="$t('register.confirmPassword')"
                                            type="password"
                                            autocomplete="new-password"
                                            hide-details="auto"
                                            density="compact"
                                            @paste.prevent
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="12" :md="firebaseUid ? 12 : 6">
                                <v-btn
                                    v-if="!firebaseUid"
                                    :loading="googleLinking"
                                    @click="linkGoogleAccount"
                                >
                                    {{ $t("register.registerWithGoogle") }}
                                </v-btn>
                                <v-alert v-else type="success" variant="tonal" density="compact">
                                    {{ $t("register.accountLinked") }}: {{ firebaseEmail }}
                                </v-alert>
                            </v-col>
                        </v-row>
                        <v-row class="d-flex justify-center mt-1 pt-1">
                            <v-col class="d-flex justify-center align-center">
                                <p style="font-size: 1rem">
                                    {{ $t("register.termsAndConditions") }}
                                    <TextLink
                                        :link="{
                                            href: '/policy/terms',
                                            text: $t(
                                                'register.termsAndConditionsLink'
                                            ),
                                        }"
                                    />
                                    {{ $t("register.and") }}
                                    <TextLink
                                        :link="{
                                            href: '/policy/privacy',
                                            text: $t(
                                                'register.privacyPolicyLink'
                                            ),
                                        }"
                                    />
                                </p>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions class="justify-center">
                        <v-btn
                            v-if="!prefill"
                            data-cy="register-submit"
                            variant="flat"
                            :slim="false"
                            style="
                                text-transform: unset;
                                font-weight: 600;
                                letter-spacing: unset;
                            "
                            @click="handleRegister"
                            >{{ $t("register.register") }}
                        </v-btn>
                        <!-- <v-btn
                            v-if="!prefill"
                            variant="flat"
                            color="warning"
                            :slim="false"
                            style="
                                text-transform: unset;
                                font-weight: 600;
                                letter-spacing: unset;
                            "
                            @click="autoFillFormTesting"
                            >{{ "FillFORM" }}
                        </v-btn> -->
                        <v-btn
                            v-if="prefill"
                            variant="flat"
                            :slim="false"
                            style="
                                text-transform: unset;
                                font-weight: 600;
                                letter-spacing: unset;
                            "
                            @click="handleCompanyUpdate"
                            >{{ $t("common.save") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
            <LoadingOverlay :model-value="registerLoading" />
        </v-container>
    </v-slide-x-transition>
</template>

<script>
import { mapActions } from "vuex";
import {
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    sendEmailVerification,
    signOut,
} from "firebase/auth";
import { auth } from "@/firebase/config";
import countryCodes from "@/locales/countryCodes.json";
import CardTitle from "@/components/registration/CardTitle.vue";
import autoAnimate from "@formkit/auto-animate";
import TextLink from "@/components/registration/TextLink.vue";
import DownloadFileButton from "@/components/registration/DownloadFileButton.vue";
import SelectSpokenLanguages from "@/components/SelectSpokenLanguages.vue";
import { showAlertModal } from "@/utils";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";

export default {
    components: {
        CardTitle,
        TextLink,
        DownloadFileButton,
        SelectSpokenLanguages,
        LoadingOverlay,
    },
    created() {
        const subscriptionMonths = Number(this.$route.query.subscriptionMonths);
        if (subscriptionMonths === 6 || subscriptionMonths === 12) {
            this.companyInformation.subscriptionMonths = subscriptionMonths;
        }
        if (!this.prefill) return;

        const c = this.prefill; // shorthand

        // ─── company main fields ──────────────────────────────
        this.companyInformation.name = c.name ?? "";
        this.companyInformation.type = c.type ?? 0;
        this.companyInformation.VATNumber = c.VATNumber ?? "";

        // ─── registry block ───────────────────────────────────
        this.companyInformation.registry = {
            orderNumber: c.registry_orderNumber ?? "",
            uniqueId: c.registry_uniqueId ?? "",
            year: c.registry_year ?? "",
        };

        // ─── address block ────────────────────────────────────
        this.companyInformation.address = {
            street: c.street ?? "",
            number: c.number ?? "",
            postalCode: c.postal_code ?? "",
            city: c.city ?? "",
            country: c.country ?? "",
            fax: c.fax ?? "",
        };

        // ─── administrator (if the API sent one) ──────────────
        if (c.admin_user) {
            this.contact.firstName = c.admin_user.first_name || "";
            this.contact.lastName = c.admin_user.last_name || "";
            this.contact.email = c.admin_user.email || "";
            this.contact.position = c.admin_user.job_title || "";
            this.contact.phone_number = c.admin_user.phone || "";
        }
    },
    // add a timer on created for 300 ms
    mounted() {
        this.showComponent = true;
        // Grab the internal element with the class "v-field__input" and apply autoAnimate
        this.$nextTick(() => {
            // Log the entire v-file-input element
            if (this.$refs.fileInput) {
                // Query for the child element with the class .v-field__input
                const fieldInput =
                    this.$refs.fileInput.$el.querySelector(".v-field__input");
                if (fieldInput) {
                    // You can now apply autoAnimate or other logic to fieldInput
                    autoAnimate(fieldInput);
                }
            }
        });
    },
    data() {
        return {
            registryGroupErrors: {
                orderNumber: "",
                uniqueId: "",
                year: "",
            },
            filesWatcherKey: 0,
            showComponent: false,
            registerLoading: false,
            contact: {
                firstName: "",
                lastName: "",
                email: "",
                position: "",
                phone_prefix: "+40",
                phone_number: "",
                spoken_languages: [],
            },
            password: "",
            confirmPassword: "",
            companyInformation: {
                name: "",
                type: 1,
                VATNumber: "",
                registry: {
                    orderNumber: "",
                    uniqueId: "",
                    year: "",
                },
                address: {
                    street: "",
                    number: "",
                    postalCode: "",
                    city: "",
                    country: "",
                    fax: "",
                    website: "",
                },
                files: [],
                subscriptionMonths: 6,
            },
            isValid: false,
            errors: {},
            firebaseUid: null,
            firebaseEmail: null,
            googleLinking: false,
            BACKEND_ERROR_MAP: {
                // Romanian message you showed
                "Company cu VATNumber deja există.":
                    "register.backend.errors.VATNumberExists",
                "A company with this VAT number already exists.":
                    "register.backend.errors.VATNumberExists",
                "This registry combination already exists.":
                    "register.backend.errors.registryCombinationExists",
                "contact cu email deja există.":
                    "register.backend.errors.emailExists",
                "A contact with this email already exists.":
                    "register.backend.errors.emailExists",
            },
            COMPANY_FIELDS: [
                "name",
                "VATNumber",
                "type",
                "registry_orderNumber",
                "registry_uniqueId",
                "registry_year",
                "street",
                "number",
                "postal_code",
                "city",
                "country",
                "fax",
            ],
            CONTACT_FIELDS: [
                "first_name",
                "last_name",
                "email",
                "position",
                "phone_number",
                "spoken_languages",
            ],
            fileInputKey: 0,
            fileErrors: "",
        };
    },
    props: {
        prefill: Object,
        readonly: Boolean,
    },
    emits: ["triggered-company-update"],
    computed: {
        subscriptionPeriods() {
            return [
                { text: "6", value: 6 },
                { text: "12", value: 12 },
            ];
        },
        websiteRules() {
            return [
                (v) =>
                    !v ||
                    /^(https?:\/\/)?[^\s]+\.[^\s]+/i.test(v) ||
                    this.$t("register.invalidUrl"),
            ];
        },
        addressRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) => v.length > 2 || this.$t("validation.addressMinLength"),
            ];
        },
        rulesLocaleKey() {
            return this.$i18n.locale;
        },
        companyTypes() {
            return [
                {
                    text: this.$t("register.companyType.transportator"),
                    value: 0,
                },
                { text: this.$t("register.companyType.speditie"), value: 1 },
                { text: this.$t("register.companyType.producator"), value: 2 },
            ];
        },
        companyNameRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) => v.length >= 5 || this.$t("validation.companyNameLength"),
            ];
        },
        vatRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) =>
                    /^[A-Z]{2}[A-Z0-9]{6,12}$/i.test(v) ||
                    this.$t("validation.invalidVatNumber"),
            ];
        },
        emailRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) =>
                    /.+@.+\..+/.test(v) || this.$t("validation.invalidEmail"),
            ];
        },
        passwordRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) => !v || v.length >= 8 || this.$t("validation.passwordMinLength"),
                (v) => !v || /[A-Z]/.test(v) || this.$t("validation.passwordUppercase"),
                (v) => !v || /[a-z]/.test(v) || this.$t("validation.passwordLowercase"),
                (v) => !v || /\d/.test(v) || this.$t("validation.passwordDigit"),
                (v) => !v || /[!@#$%^&*(),.?":{}|<>]/.test(v) || this.$t("validation.passwordSpecial"),
            ];
        },
        confirmPasswordRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) => v === this.password || this.$t("validation.passwordsMustMatch"),
            ];
        },
        nameRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) => v.length >= 3 || this.$t("validation.minLength"),
            ];
        },
        positionRules() {
            return [
                (v) => !!String(v || "").trim() || this.$t("validation.required"),
            ];
        },
        phoneNumberRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) =>
                    /^[0-9]{6,15}$/.test(v) ||
                    this.$t("validation.invalidPhoneNumber"),
            ];
        },
        phonePrefixRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) =>
                    /^\+\d{1,4}$/.test(v) ||
                    this.$t("validation.invalidPrefix"),
            ];
        },
        documentRules() {
            return [
                (v) => !!v || this.$t("validation.required"),
                (v) => {
                    if (this.companyInformation.type === 0) {
                        return (
                            v.length === 3 ||
                            this.$t(
                                "validation.insufficientTransporterDocuments"
                            ) +
                                ": " +
                                this.$t("validation.transporterDocuments")
                        );
                    } else if (this.companyInformation.type === 1) {
                        return (
                            v.length === 3 ||
                            this.$t(
                                "validation.insufficientSpeditionDocuments"
                            ) +
                                ": " +
                                this.$t("validation.speditionDocuments")
                        );
                    } else if (this.companyInformation.type === 2) {
                        return (
                            v.length === 2 ||
                            this.$t(
                                "validation.insufficientProducerDocuments"
                            ) +
                                ": " +
                                this.$t("validation.producerDocuments")
                        );
                    }
                    return true;
                },
                (v) => {
                    if (!v || !v.length) return true;
                    if (Array.isArray(v)) {
                        return (
                            v.every(
                                (file) => file.type === "application/pdf"
                            ) || this.$t("validation.invalidDocument")
                        );
                    } else {
                        return (
                            v.type === "application/pdf" ||
                            this.$t("validation.invalidDocument")
                        );
                    }
                },
            ];
        },
        registryGroupErrorList() {
            return Object.values(this.registryGroupErrors).filter(
                (msg) => !!msg
            );
        },
        isFormValid() {
            return this.isValid; // This will update automatically when validation rules pass
        },
        displayNecessaryDocumentsHint() {
            if (this.companyInformation.type === 0) {
                return this.$t("validation.transporterDocuments");
            } else if (this.companyInformation.type === 1) {
                return this.$t("validation.speditionDocuments");
            } else if (this.companyInformation.type === 2) {
                return this.$t("validation.producerDocuments");
            }
            return true;
        },
        getCountryDialCode() {
            return this.contact.phone_prefix || "";
        },
        europeanCountryCodes() {
            // Define an array of European country codes
            const europeanCodes = [
                "AL",
                "AD",
                "AT",
                "BY",
                "BE",
                "BA",
                "BG",
                "CH",
                "CY",
                "CZ",
                "DE",
                "XK",
                "DK",
                "EE",
                "ES",
                "FI",
                "FR",
                "GR",
                "HR",
                "HU",
                "IE",
                "IS",
                "IT",
                "PL",
                "LI",
                "LT",
                "LU",
                "LV",
                "MC",
                "MD",
                "ME",
                "MK",
                "MT",
                "NL",
                "NO",
                "PT",
                "RO",
                "RS",
                "RU",
                "SE",
                "SI",
                "SK",
                "SM",
                "TR",
                "UA",
                "VA",
                "GB",
            ];
            // Return the filtered list of country codes that are in Europe
            return countryCodes.filter((country) =>
                europeanCodes.includes(country.code)
            );
        },
        /** how many PDFs this company type must attach */
        requiredDocCount() {
            // transporter / spedition → 3, producer → 2
            return this.companyInformation.type === 2 ? 2 : 3;
        },
        /** show the picker only while we still need at least one more file */
        showFileInput() {
            return this.companyInformation.files.length < this.requiredDocCount;
        },
    },
    watch: {
        files: {
            handler() {
                this.filesWatcherKey++;
            },
            deep: true,
        },
        rulesLocaleKey() {
            this.$nextTick(() => {
                if (this.$refs.form) {
                    this.$refs.form.validate();
                }
            });
        },
    },
    methods: {
        showAlertModal,
        /**
         * Merge the newly-picked files into the real list
         * and then reset the <v-file-input>.
         */
        handleFileSelect(newFiles) {
            if (!newFiles) return;

            (Array.isArray(newFiles) ? newFiles : [newFiles]).forEach((f) => {
                // skip duplicates by name
                if (
                    !this.companyInformation.files.some(
                        (d) => d.name === f.name
                    )
                ) {
                    this.companyInformation.files.push(f);
                }
            });

            // wipe the picker so the same file can be chosen again
            this.fileInputKey += 1;
            // clear any “too few docs” message as soon as user adds a file
            this.fileErrors = "";
        },
        translateBackendMessage(msg, vm) {
            // vm === `this` inside a component (needed for $t / $te)
            const key = this.BACKEND_ERROR_MAP[msg];
            return key && vm.$te(key) ? vm.$t(key) : msg; // fallback → show raw
        },
        async handleCompanyUpdate() {
            const form = this.$refs.form;
            if (!this.validateDocuments()) return;

            //Validate registry block FIRST
            const registryValid = this.validateRegistryNumber();

            // Then validate the rest of the form
            const formResult = await form?.validate();

            // If either fails, stop here
            if (!registryValid || !formResult.valid) return;

            // Otherwise proceed with the registration request
            this.errors = {};

            const fd = this.buildFormData(this);

            this.$emit("triggered-company-update", fd);
        },
        async linkGoogleAccount() {
            this.googleLinking = true;
            try {
                const provider = new GoogleAuthProvider();
                const result = await signInWithPopup(auth, provider);
                this.firebaseUid = result.user.uid;
                this.firebaseEmail = result.user.email;
                this.contact.email = result.user.email || this.contact.email;
            } catch (err) {
                if (err?.code !== "auth/popup-closed-by-user") {
                    this.showAlertModal(this.$store, err?.message || this.$t("register.linkAccountFailed"), "danger", 8000, err);
                }
            } finally {
                this.googleLinking = false;
            }
        },
        buildFormData({ companyInformation, contact, firebaseUid, password }) {
            const fd = new FormData();

            const normalizedPhoneNumber = contact.phone_number
                .replace(/[\s-]+/g, "")
                .replace(/^0+/, "");

            const block = {
                company: {
                    name: companyInformation.name,
                    type: companyInformation.type,
                    VATNumber: companyInformation.VATNumber,
                    registry_orderNumber: companyInformation.registry.orderNumber,
                    registry_uniqueId: companyInformation.registry.uniqueId,
                    registry_year: companyInformation.registry.year,
                    street: companyInformation.address.street,
                    number: companyInformation.address.number,
                    postal_code: companyInformation.address.postalCode,
                    city: companyInformation.address.city,
                    country: companyInformation.address.country,
                    fax: companyInformation.address.fax,
                    website: companyInformation.website,
                    subscription_months: companyInformation.subscriptionMonths,
                },
                contact: {
                    first_name: contact.firstName,
                    last_name: contact.lastName,
                    email: contact.email,
                    position: contact.position,
                    phone_prefix: contact.phone_prefix,
                    phone_number: normalizedPhoneNumber,
                    spoken_languages: contact.spoken_languages,
                },
                firebase_uid: firebaseUid || null,
            };
            if (!firebaseUid && password) {
                block.password = password;
            }
            fd.append("data", JSON.stringify(block));

            (companyInformation.files || []).forEach((f) =>
                fd.append("documents", f)
            );
            return fd;
        },
        validateRegistryNumber() {
            const { orderNumber, uniqueId, year } =
                this.companyInformation.registry;

            // Reset all errors first
            this.registryGroupErrors = {
                orderNumber: "",
                uniqueId: "",
                year: "",
            };

            if (!orderNumber || !/^[JF][0-9]{2}$/.test(orderNumber)) {
                this.registryGroupErrors.orderNumber =
                    "validation.invalidRegistryOrderNumber";
            }

            if (!uniqueId || !/^[0-9]{1,5}$/.test(uniqueId.toString())) {
                this.registryGroupErrors.uniqueId =
                    "validation.invalidUniqueId";
            }

            if (!year || !/^[0-9]{4}$/.test(year.toString())) {
                this.registryGroupErrors.year = "validation.invalidYear";
            }

            return this.registryGroupErrorList.length === 0;
        },

        ...mapActions("auth", ["register"]), // Calls Vuex store action
        /**
         * Auto-fills the registration form with randomized test data for both company and contact information.
         */
        autoFillFormTesting() {
            // auto fill the form with testing data but randomize the data
            this.companyInformation.name =
                "Test Company " + Math.random().toString(36).substring(2, 15);
            this.companyInformation.type = Math.floor(Math.random() * 3);
            this.companyInformation.VATNumber =
                "RO" + Math.floor(Math.random() * 10000000000);
            this.companyInformation.registry.orderNumber =
                "J" + Math.floor(Math.random() * 100);
            this.companyInformation.registry.uniqueId = Math.floor(
                Math.random() * 100000
            );
            this.companyInformation.registry.year = "2024";
            this.companyInformation.files = [
                { name: "test.pdf", type: "application/pdf" },
                { name: "test2.pdf", type: "application/pdf" },
            ];
            this.contact.firstName = "John";
            this.contact.lastName = "Doe";
            this.contact.position = "Manager";
            this.contact.phone_number = "1234567890";
            this.contact.email = "john.doe@example.com";
            this.contact.phone_prefix = "+40";
            this.companyInformation.address.country = "Romania";
            this.companyInformation.address.city = "Timis";
            this.companyInformation.address.postalCode = "320067";
            this.companyInformation.address.street = "Macilor";
            this.companyInformation.address.number = "9";
            this.companyInformation.address.fax = "0123-456-7890";
        },
        /**
         * Removes a file from the company files array based on its name.
         * @param name
         */
        removeFile(name) {
            this.companyInformation.files =
                this.companyInformation.files.filter((f) => f.name !== name);
            // if quota is no longer met, show the picker again
            if (this.showFileInput) this.fileErrors = "";
        },
        validateDocuments() {
            const n = this.companyInformation.files.length;
            if (n >= this.requiredDocCount) return true;

            // Build the same text you already show in `documentRules`
            const msgKey = {
                0: "validation.insufficientTransporterDocuments",
                1: "validation.insufficientSpeditionDocuments",
                2: "validation.insufficientProducerDocuments",
            }[this.companyInformation.type];

            this.fileErrors = `${this.$t(msgKey)}: ${
                this.displayNecessaryDocumentsHint
            }`;
            return false;
        },
        /**
         * Handles the registration form submission.
         * Validates the form, constructs a FormData payload including company info, contact info, and files,
         * then sends a registration request to the API. On success, it alerts the user and navigates to the pending registration page.
         */
        async handleRegister() {
            const form = this.$refs.form;
            if (!this.validateDocuments()) return;

            let registryValid;
            if (this.companyInformation.VATNumber.startsWith("RO")) {
                //Validate registry block FIRST
                registryValid = this.validateRegistryNumber();
            } else {
                registryValid = true;
            }

            // Then validate the rest of the form
            const formResult = await form?.validate();

            // If either fails, stop here
            if (!registryValid || !formResult.valid) return;

            // Otherwise proceed with the registration request
            this.errors = {};
            this.registerLoading = true;

            try {
                // Create FormData instead of a normal JS object
                const formData = this.buildFormData(this);

                const registerRequest = await this.register(formData);
                if (registerRequest.status === 201) {
                    if (!this.firebaseUid && this.contact.email && this.password) {
                        try {
                            await signInWithEmailAndPassword(
                                auth,
                                this.contact.email,
                                this.password
                            );
                            const user = auth.currentUser;
                            if (user) {
                                const basePath = (
                                    process.env.VUE_APP_BASE_PATH || "/"
                                ).replace(/\/$/, "");
                                const continueUrl = `${window.location.origin}${basePath}/registration-pending`;
                                await sendEmailVerification(user, {
                                    url: continueUrl,
                                });
                                await signOut(auth);
                            }
                        } catch (err) {
                            console.warn("Could not send verification email:", err);
                        }
                    }
                    this.showAlertModal(
                        this.$store,
                        this.$t("register.registrationSuccess"),
                        "success",
                        5000
                    );
                    setTimeout(() => {
                        this.$router.push("/registration-pending");
                    }, 1000);
                } else {
                    this.errors = { companyInformation: {}, contact: {}, password: [] };

                    if (registerRequest.response && registerRequest.response.data) {
                        const apiErrors = registerRequest.response.data;

                        Object.entries(apiErrors).forEach(([field, messages]) => {
                            const nice = messages.map((m) =>
                                this.translateBackendMessage(m, this)
                            );

                            if (field === "registry_combination") {
                                this.registryGroupErrors.uniqueId = nice[0];
                                return;
                            }

                            if (this.COMPANY_FIELDS.includes(field)) {
                                this.errors.companyInformation[field] = nice;
                            } else if (this.CONTACT_FIELDS.includes(field)) {
                                this.errors.contact[field] = nice;
                            } else {
                                this.errors[field] = nice;
                            }
                        });
                    }

                    const firstErrorMsg = this.translateBackendMessage(
                        Object.values(this.errors.companyInformation)[0]?.[0] ||
                            Object.values(this.errors.contact)[0]?.[0] ||
                            this.errors.password?.[0] ||
                            Object.values(this.errors)[0]?.[0] ||
                            this.$t("register.genericError"),
                        this
                    );

                    this.showAlertModal(
                        this.$store,
                        firstErrorMsg,
                        "danger",
                        12000,
                        registerRequest
                    );
                }
            } finally {
                this.registerLoading = false;
            }
        },
    },
};
</script>

<style scoped>
.order-number {
    margin-right: 0;
    padding-right: 0;
}

.order-number .v-field {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: none !important;
}

.unique-id {
    margin-left: 0;
    padding-left: 0;
    margin-right: 0;
    padding-right: 0;
}

.unique-id .v-field {
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;
}

.year {
    margin-left: 0;
    padding-left: 0;
}

.year .v-field {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-left: none !important;
}
</style>
<style>
.company-files .v-field__input {
    z-index: 2;
}

.order-number .v-field {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
}

.order-number .v-field .v-field__outline__end {
    border-right-width: 0 !important;
}

.unique-id .v-field {
    border-radius: 0 !important;
}

.unique-id .v-field .v-field__outline__end,
.unique-id .v-field .v-field__outline__start {
    border-right-width: 0 !important;
    border-left-width: 0 !important;
}

.year .v-field {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
}

.year .v-field .v-field__outline__start {
    border-left-width: 0 !important;
}
</style>